import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import {
  FrappeGanttView,
  type GanttViewMode,
} from "@/components/FrappeGanttView";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { buildPortfolio, type PortfolioTask } from "@/lib/portfolioGantt";

const EMPTY_EDGES: { blockerId: string; blockedId: string }[] = [];
const EMPTY_CRIT = new Set<string>();

/**
 * Portfolio Gantt (task_61bjlu) — every active project's dated tasks on one
 * timeline, grouped and coloured by project. Toggle projects on/off; click a
 * bar to open the task; drag to reschedule (persists to the task's own
 * project). A cross-project planning view distinct from the project-level
 * Roadmap.
 */
function PortfolioView() {
  const [viewMode, setViewMode] = useState<GanttViewMode>("Month");
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const projectsQ = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });

  const projects = useMemo(
    () =>
      (projectsQ.data?.projects ?? []).filter((p) => p.status !== "archived"),
    [projectsQ.data],
  );

  // One tasks query per project, in parallel.
  const taskQueries = useQueries({
    queries: projects.map((p) => ({
      queryKey: queryKeys.tasksFor(p.id),
      queryFn: () => api.listTasks(p.id),
    })),
  });

  const tasksByProject = useMemo(() => {
    const m = new Map<string, PortfolioTask[]>();
    projects.forEach((p, i) => {
      const data = taskQueries[i]?.data;
      if (data) m.set(p.id, data.tasks as PortfolioTask[]);
    });
    return m;
    // taskQueries' array identity changes every render, so key the memo on the
    // data we actually read (each query's last-updated stamp).
  }, [projects, taskQueries.map((q) => q.dataUpdatedAt).join(",")]);

  const { tasks, meta } = useMemo(
    () => buildPortfolio(projects, tasksByProject, hidden),
    [projects, tasksByProject, hidden],
  );

  const users = useMemo(() => {
    const map = new Map<string, { name: string; image: string | null }>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, {
        name: m.name ?? m.email ?? m.id.slice(0, 8),
        image: m.image ?? null,
      });
    }
    return map;
  }, [teamQ.data]);

  const loading = projectsQ.isLoading || taskQueries.some((q) => q.isLoading);

  function toggle(id: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="portfolio-page">
      <div className="rm-head">
        <h1 className="pg-title" style={{ margin: 0 }}>
          Portfolio
        </h1>
        <span className="t-sm t-muted">
          Every active project&rsquo;s tasks on one timeline.
        </span>
      </div>

      {projects.length > 0 && (
        <div className="pf-legend" role="group" aria-label="Toggle projects">
          {projects.map((p) => {
            const off = hidden.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                className={`pf-chip${off ? " off" : ""}`}
                aria-pressed={!off}
                onClick={() => toggle(p.id)}
                title={off ? `Show ${p.name}` : `Hide ${p.name}`}
              >
                <span
                  className="pf-dot"
                  style={{ background: p.color ?? "#2563eb" }}
                />
                {p.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="portfolio-canvas">
        {loading ? (
          <Skeleton count={6} height={40} />
        ) : tasks.length === 0 ? (
          <EmptyState
            icon="chev"
            title="Nothing scheduled"
            caption="Tasks with a start or due date across your active projects appear here. Add dates to see bars."
          />
        ) : (
          <FrappeGanttView
            projectId=""
            tasks={tasks}
            edges={EMPTY_EDGES}
            milestones={[]}
            users={users}
            criticalIds={EMPTY_CRIT}
            showCritical={false}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            tasksMeta={meta}
          />
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/portfolio")({
  component: PortfolioView,
});
